import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Banner, Button, ButtonLink, Card, Dialog, Field, formatMoney, Money, PageHeader, type LinkComponent } from '@elysian/ui';
import type { BankTransactions as Txns, BankTransactionsQuery } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, shortDate } from '../invoices/shared';
import { ExportMenu } from '../shared/ExportMenu';
import type { ExportColumn } from '../shared/exportList';

const RouterLink = Link as unknown as LinkComponent;
type Line = Txns['lines'][number];
const SIZES = [{ key: 50, label: '50' }, { key: 100, label: '100' }, { key: 0, label: 'All' }] as const;
const SIZE_KEY = 'elysian.bank-transactions.size';
const money = (n: number | null | undefined) => (n === null || n === undefined ? '' : n.toFixed(2));

const SOURCE: Record<string, string> = {
  receipt: 'Receipt', payment: 'Payment', bank_transfer: 'Transfer', manual: 'Journal', reversal: 'Reversal',
  opening_balance: 'Opening balance', purchase_bill: 'Bill paid', sales_invoice: 'Invoice paid', fx_revaluation: 'Revaluation',
  fx_realised: 'Exchange difference', year_end: 'Year end', purchase_credit: 'Supplier credit', sales_credit: 'Credit note',
};

/** Where a line's document or journal opens. */
function linkFor(l: Line): string {
  const d = l.document;
  if (!d) return `/journals?open=${encodeURIComponent(l.journal_id)}`;
  return d.type === 'sales_invoice' ? `/invoices/${d.id}` : d.type === 'sales_credit' ? `/credit-notes/${d.id}`
    : d.type === 'receipt' || d.type === 'payment' ? `/receipts/${d.id}` : `/bills/${d.id}`;
}

const columns: ExportColumn<Line>[] = [
  { header: 'Date', kind: 'date', value: (l) => l.date },
  { header: 'Type', value: (l) => SOURCE[l.source] ?? l.source },
  { header: 'Number', value: (l) => l.document?.number ?? l.journal_no },
  { header: 'Details', value: (l) => l.description },
  { header: 'Who', value: (l) => l.contact },
  { header: 'Money in', kind: 'money', value: (l) => money(l.money_in) || null },
  { header: 'Money out', kind: 'money', value: (l) => money(l.money_out) || null },
  { header: 'Balance', kind: 'money', value: (l) => money(l.balance) || null },
  { header: 'Reconciled', value: (l) => (l.reconciled === null ? '' : l.reconciled ? `Yes${l.reconciled_to ? ` (${l.reconciled_to})` : ''}` : 'No') },
];

function savedSize(): number {
  try { const n = Number(localStorage.getItem(SIZE_KEY)); return [50, 100, 0].includes(n) && localStorage.getItem(SIZE_KEY) !== null ? n : 50; } catch { return 50; }
}

/**
 * A bank account's transactions in the books (#51): every receipt, payment, transfer and journal on it,
 * newest first, with a running balance and whether each is reconciled; a date range, "not reconciled only",
 * 50, 100 or all at a time, and an export of everything that matches.
 */
export function BankTransactions() {
  const { code = '' } = useParams();
  const { source, today, can } = useSession();
  const toast = useToast((x) => x.show);
  const now = iso(today());
  const mayReconcile = can('bank.reconcile');
  // #53: reconciling by hand against a paper or PDF statement.
  const [hand, setHand] = useState<{ date: string; balance: string; ticked: Set<string> } | null>(null);
  const [handError, setHandError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [unticking, setUnticking] = useState<Line | null>(null);
  const [version, setVersion] = useState(0);
  const [from, setFrom] = useState(`${now.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(now);
  const [unreconciled, setUnreconciled] = useState(false);
  const [size, setSize] = useState<number>(savedSize);
  const [pageNo, setPageNo] = useState(0);
  const rangeError = from && to && from > to ? 'The start is after the end' : null;
  const query: BankTransactionsQuery = { ...(from ? { from } : {}), ...(to ? { to } : {}), ...(unreconciled ? { unreconciled: true } : {}) };
  // By hand: every entry not yet reconciled up to the statement date, all on one page.
  const handDate = hand?.date && /^\d{4}-\d{2}-\d{2}$/.test(hand.date) ? hand.date : now;
  const data = useAsync(() => (hand ? source.bankTransactions(code, { unreconciled: true, to: handDate })
    : rangeError ? Promise.resolve(null)
      : source.bankTransactions(code, { ...query, ...(size ? { limit: size, offset: pageNo * size } : {}) })),
  [source, code, from, to, unreconciled, size, pageNo, rangeError, hand ? handDate : null, Boolean(hand), version], { keep: true });

  useEffect(() => { setPageNo(0); }, [from, to, unreconciled, size]);
  useEffect(() => { try { localStorage.setItem(SIZE_KEY, String(size)); } catch { /* not remembered: fine */ } }, [size]);

  const t = data.state === 'ready' ? data.data : null;
  const cur = t?.account.currency;
  const pages = t && size ? Math.max(1, Math.ceil(t.count / size)) : 1;
  const first = t && t.count ? pageNo * (size || t.count) + 1 : 0;
  const last = t ? first + t.lines.length - 1 : 0;

  // The balance check (#53): the statement's closing balance less the reconciled balance and the entries ticked.
  const amount = (l: Line) => (l.money_in ?? 0) - (l.money_out ?? 0);
  const tickedLines = hand && t ? t.lines.filter((l) => hand.ticked.has(l.id)) : [];
  const tickedSum = Math.round(tickedLines.reduce((x, l) => x + amount(l), 0) * 100) / 100;
  const balanceOk = hand ? /^-?\d+(\.\d{1,2})?$/.test(hand.balance.replace(/,/g, '').trim()) : false;
  const difference = hand && t && balanceOk ? Math.round((Number(hand.balance.replace(/,/g, '')) - t.reconciled_balance - tickedSum) * 100) / 100 : null;
  const tick = (id: string, on: boolean) => setHand((h) => {
    if (!h) return h;
    const n = new Set(h.ticked); if (on) n.add(id); else n.delete(id);
    return { ...h, ticked: n };
  });

  async function saveHand() {
    if (!hand) return;
    setSaving(true);
    setHandError(null);
    try {
      const r = await source.reconcileByHand(code, { statement_date: hand.date, closing_balance: hand.balance.replace(/,/g, '').trim(), journal_lines: [...hand.ticked] });
      toast(`${r.entries} ${r.entries === 1 ? 'entry' : 'entries'} reconciled to the statement of ${shortDate(r.statement_date)}`);
      setHand(null);
      setVersion((v) => v + 1);
    } catch (e) {
      setHandError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader title={t ? t.account.name : 'Bank account'}
        subtitle={t ? `${t.account.code} · ${t.account.currency} · every entry on this account in the books` : 'Transactions in the books'}
        actions={<>
          <ButtonLink as={RouterLink} to="/bank-accounts" variant="ghost">All bank accounts</ButtonLink>
          {mayReconcile && !hand && <Button onClick={() => { setHandError(null); setHand({ date: now, balance: '', ticked: new Set() }); }}>Reconcile by hand</Button>}
          <ButtonLink as={RouterLink} to={`/bank?account=${code}`} variant="primary">Reconcile</ButtonLink>
        </>} />
      {data.state === 'error' && <Banner>Couldn’t load the transactions: {data.error.message}</Banner>}

      {t && (
        <div className="el-grid" data-min="220" style={{ marginBottom: 16 }}>
          <Card className="el-stat"><div className="el-stat-label">In the books</div><div className="el-stat-value"><Money value={t.book_balance.toFixed(2)} currency={cur} /></div><div className="el-stat-note">today</div></Card>
          <Card className="el-stat"><div className="el-stat-label">Reconciled</div><div className="el-stat-value"><Money value={t.reconciled_balance.toFixed(2)} currency={cur} /></div>
            <div className="el-stat-note">{t.last_reconciled ? `to ${shortDate(t.last_reconciled)}` : 'nothing matched yet'}</div></Card>
          <Card className="el-stat"><div className="el-stat-label">Not reconciled</div><div className="el-stat-value">{t.unreconciled_count}</div>
            <div className="el-stat-note">{t.unreconciled_count === 1 ? 'entry' : 'entries'} not on a statement yet</div></Card>
        </div>
      )}
      {t && t.statement_lines_waiting > 0 && (
        <Banner action={<ButtonLink as={RouterLink} to={`/bank?account=${code}`}>Reconcile</ButtonLink>}>
          {t.statement_lines_waiting} {t.statement_lines_waiting === 1 ? 'line on the bank statement isn’t' : 'lines on the bank statement aren’t'} in the books yet.
        </Banner>
      )}

      {hand && t && (
        <div style={{ marginBottom: 16 }}><Card as="section" padding="16px" aria-labelledby="hand-title">
          <h2 id="hand-title" className="el-h2" style={{ margin: '0 0 4px' }}>Reconcile by hand</h2>
          <p className="el-hint" style={{ margin: '0 0 12px' }}>
            From a paper or PDF statement: its date and closing balance, then tick each entry below that’s on it.
            It saves when the difference is zero. Entries matched to an imported statement aren’t listed.
          </p>
          <div className="el-hand-grid">
            <Field id="hand-date" label="Statement date">
              <input id="hand-date" type="date" className="el-input" value={hand.date} max={now}
                onChange={(e) => setHand({ ...hand, date: e.target.value, ticked: new Set([...hand.ticked].filter((id) => (t.lines.find((l) => l.id === id)?.date ?? '') <= e.target.value)) })} />
            </Field>
            <Field id="hand-balance" label="Closing balance on the statement" hint={cur ? `In ${cur}; a minus sign if overdrawn` : undefined}>
              <input id="hand-balance" className="el-input" inputMode="decimal" value={hand.balance} placeholder="0.00"
                onChange={(e) => setHand({ ...hand, balance: e.target.value })} />
            </Field>
            <dl className="el-hand-sums" aria-label="The balance check">
              <div><dt>Reconciled before</dt><dd><Money value={t.reconciled_balance.toFixed(2)} currency={cur} /></dd></div>
              <div><dt>Ticked ({tickedLines.length})</dt><dd><Money value={tickedSum.toFixed(2)} currency={cur} /></dd></div>
              <div><dt>Difference</dt><dd data-zero={difference === 0 || undefined}>{difference === null ? '—' : <Money value={difference.toFixed(2)} currency={cur} />}</dd></div>
            </dl>
          </div>
          {handError && <div className="el-error-box" role="alert">{handError}</div>}
          <div className="el-row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <Button variant="ghost" onClick={() => { setHand(null); setHandError(null); }}>Cancel</Button>
            <Button variant="primary" disabled={saving || difference !== 0 || !hand.date} onClick={() => void saveHand()}>
              {saving ? 'Saving…' : difference === null ? 'Save' : difference === 0 ? `Save: ${tickedLines.length} reconciled` : `Out by ${formatMoney(difference.toFixed(2), cur)}`}
            </Button>
          </div>
        </Card></div>
      )}

      <Card>
        {!hand && <div className="el-spread" style={{ padding: '14px 16px 0', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="el-date-filter">
            <label className="el-date-filter-field">
              <span>From</span>
              <input type="date" className="el-input" aria-label="From" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="el-date-filter-field">
              <span>To</span>
              <input type="date" className="el-input" aria-label="To" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <label className="el-check"><input type="checkbox" checked={unreconciled} onChange={(e) => setUnreconciled(e.target.checked)} /> Not reconciled only</label>
            {rangeError && <span className="el-error-text" role="alert">{rangeError}</span>}
          </div>
          <div className="el-row" style={{ gap: 12, alignItems: 'center' }}>
            <label className="el-row" style={{ gap: 6, alignItems: 'center', fontSize: 14 }}>
              Show
              <select className="el-input" aria-label="Rows to show" style={{ width: 'auto' }} value={size} onChange={(e) => setSize(Number(e.target.value))}>
                {SIZES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <ExportMenu name={`${t?.account.name ?? 'Bank'} transactions`} columns={columns} rows={t?.lines ?? []} disabled={!t}
              all={t ? { count: t.count, load: async () => (await source.bankTransactions(code, query)).lines } : undefined} />
          </div>
        </div>}
        {t && t.from && !unreconciled && !hand && (
          <p className="el-hint" style={{ margin: '10px 16px 0' }}>
            Brought forward at {shortDate(t.from)}: <Money value={t.opening_balance.toFixed(2)} currency={cur} />.
            In <Money value={t.money_in.toFixed(2)} currency={cur} />, out <Money value={t.money_out.toFixed(2)} currency={cur} />,
            {t.to ? ` at ${shortDate(t.to)}` : ' at the end'}: <Money value={t.closing_balance.toFixed(2)} currency={cur} />.
          </p>
        )}
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Bank transactions">
            <thead>
              <tr>{hand && <th scope="col" className="el-tick-col"><span className="el-vh">On the statement</span></th>}<th scope="col">Date</th><th scope="col">Details</th><th scope="col" className="el-num">Money in</th><th scope="col" className="el-num">Money out</th>
                <th scope="col" className="el-num">Balance</th><th scope="col">Reconciled</th></tr>
            </thead>
            <tbody>
              {data.state === 'loading' && <tr><td colSpan={hand ? 7 : 6}><div className="el-skeleton" style={{ height: 18 }} aria-label="Loading transactions" /></td></tr>}
              {t && t.lines.length === 0 && <tr><td colSpan={hand ? 7 : 6} className="el-muted">{hand ? 'Nothing left to reconcile up to that date.' : unreconciled ? 'Everything in these dates is reconciled.' : 'Nothing on this account in these dates.'}</td></tr>}
              {t && t.lines.map((l) => (
                <tr key={l.id} aria-selected={hand?.ticked.has(l.id) || undefined}>
                  {hand && (
                    <td className="el-tick-col">
                      {l.reconciled === false && (
                        <input type="checkbox" aria-label={`On the statement: ${SOURCE[l.source] ?? l.source} ${l.document?.number ?? l.journal_no}, ${shortDate(l.date)}`}
                          checked={hand.ticked.has(l.id)} onChange={(e) => tick(l.id, e.target.checked)} />
                      )}
                    </td>
                  )}
                  <td style={{ whiteSpace: 'nowrap' }}>{shortDate(l.date)}</td>
                  <td>
                    <Link className="el-row-link" to={linkFor(l)}>{SOURCE[l.source] ?? l.source} {l.document?.number ?? l.journal_no}</Link>
                    <div className="el-hint">{[l.contact, l.description].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className="el-num">{l.money_in ? <Money value={l.money_in.toFixed(2)} currency={cur} /> : ''}</td>
                  <td className="el-num">{l.money_out ? <Money value={l.money_out.toFixed(2)} currency={cur} /> : ''}</td>
                  <td className="el-num">{l.balance === null || l.balance === undefined ? '—' : <Money value={l.balance.toFixed(2)} currency={cur} />}</td>
                  <td>
                    {l.reconciled === null ? <span className="el-muted">—</span>
                      : l.reconciled
                        ? <>
                            <span className="el-reconciled" title={l.reconciled_how === 'hand' ? `Reconciled by hand to the statement of ${shortDate(l.reconciled_to ?? l.date)}`
                              : l.reconciled_to ? `Matched to the statement of ${shortDate(l.reconciled_to)}` : 'Matched to the statement'}>
                              <Check size={16} aria-hidden="true" /> {l.reconciled_to ? shortDate(l.reconciled_to) : 'Yes'}
                            </span>
                            {l.reconciled_how === 'hand' && <div className="el-hint">By hand{mayReconcile && !hand && <> · <button type="button" className="el-link-button" onClick={() => setUnticking(l)}>Untick</button></>}</div>}
                          </>
                        : <span className="el-muted">Not yet</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {t && t.count > 0 && !hand && (
          <div className="el-spread" style={{ padding: '10px 16px 14px', alignItems: 'center' }}>
            <span className="el-hint" role="status">Showing {first}–{last} of {t.count}</span>
            {size > 0 && pages > 1 && (
              <div className="el-row" style={{ gap: 8 }}>
                <Button size="sm" disabled={pageNo === 0} onClick={() => setPageNo((p) => p - 1)}>Previous</Button>
                <Button size="sm" disabled={pageNo + 1 >= pages} onClick={() => setPageNo((p) => p + 1)}>Next</Button>
              </div>
            )}
          </div>
        )}
      </Card>
      {unticking && (
        <UntickDialog line={unticking} onClose={() => setUnticking(null)}
          onUntick={async (reason) => {
            await source.untickByHand(code, unticking.id, reason);
            toast(`${SOURCE[unticking.source] ?? unticking.source} ${unticking.document?.number ?? unticking.journal_no} unticked: not reconciled again`);
            setUnticking(null);
            setVersion((v) => v + 1);
          }} />
      )}
    </>
  );
}

/** Untick an entry reconciled by hand (#53), with a reason for the audit trail. */
function UntickDialog({ line, onClose, onUntick }: { line: Line; onClose: () => void; onUntick: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = `${SOURCE[line.source] ?? line.source} ${line.document?.number ?? line.journal_no}`;
  return (
    <Dialog title={`Untick ${name}?`} onClose={onClose}
      actions={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={busy || reason.trim().length < 3}
          onClick={async () => { setBusy(true); setError(null); try { await onUntick(reason.trim()); } catch (e) { setError(e instanceof Error ? e.message : String(e)); setBusy(false); } }}>
          Untick
        </Button>
      </>}>
      <p style={{ marginTop: 0 }}>It was reconciled by hand to the statement of {line.reconciled_to ? shortDate(line.reconciled_to) : '—'}. It goes back to not reconciled, and the audit trail keeps who unticked it and why.</p>
      <Field id="untick-reason" label="Reason">
        <input id="untick-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Such as: ticked the wrong 40.00" />
      </Field>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
